import { convertThreeViewportToMapbox } from '@core/logic';
import { processingResultSelectors } from '@processingResults/redux';
import type { ProcessingResult } from '@processingResults/types';
import { scanWithinBounds } from '@scans/helper';
import type { Scan } from '@scans/types';
import { getReducedViewport } from '@trenches/components/connect';
import { viewer3dSelectors } from '@viewer3D/redux';
import { BoundingBoxWithHeight } from '@viewer3D/types';
import { useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual, useSelector } from 'react-redux';

export const MIN_ZOOM_FOR_RENDERING = 17.5;
export const MAX_ZOOM_FOR_RENDERING = 21;
export const IS_JEST_TEST = process.env.NODE_ENV === 'test';

const getScansToRender = (
  scans: Scan[],
  cameraViewport?: BoundingBoxWithHeight,
  processingResults?: Record<string, ProcessingResult> | null,
) => {
  if (IS_JEST_TEST) {
    return scans;
  }

  const { zoom } = convertThreeViewportToMapbox(cameraViewport);

  if (zoom && zoom > MAX_ZOOM_FOR_RENDERING) {
    return scans;
  }

  const reducedViewport = cameraViewport ? getReducedViewport(cameraViewport) : undefined;
  const scansInViewport = reducedViewport
    ? scans.filter(scanWithinBounds(reducedViewport, processingResults))
    : scans;
  const scansFilteredByZoom = zoom && zoom < MIN_ZOOM_FOR_RENDERING ? [] : scansInViewport;
  return scansFilteredByZoom;
};

export const useScanIdsWithTexturedMeshInScope = (scans: Scan[]): string[] => {
  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const processingResults = useSelector(processingResultSelectors.selectEntities);

  // 1. Memoize scansToRender to keep its reference stable if inputs don't change
  const scansToRender = useMemo(() => {
    return getScansToRender(scans, cameraViewport, processingResults);
  }, [scans, cameraViewport, processingResults]);

  // 2. Memoize the selector factory, moving the array .map() inside the selector
  const selectScanIds = useMemo(() => {
    return createSelector([processingResultSelectors.selectAll], (results) =>
      results
        .filter(
          (p) =>
            scansToRender.some((s) => s.selectedProcessingResultId === p.id) &&
            p?.texturedMeshes?.length > 0,
        )
        .map(({ scanId }) => scanId),
    );
  }, [scansToRender]);

  // 3. Use shallowEqual to guarantee exact referential identity unless scan IDs actually change
  return useSelector(selectScanIds, shallowEqual);
};
